/**
 * Badge color variants. Names mirror Figma `comp/badge` variant values 1:1 —
 * including the positional `accent-1` / `accent-2` (no semantic aliasing) — so
 * design ↔ code mapping stays mechanical.
 *
 * Shared by both {@link Badge} (text pills) and {@link IconBadge} (square
 * icon tiles), except `tertiary-mono` which is text-only.
 */
export type BadgeVariant =
  | "primary"
  | "positive"
  | "secondary"
  | "tertiary"
  | "tertiary-mono"
  | "warning"
  | "destructive"
  | "accent-1"
  | "accent-2";

interface VariantTokens {
  /** Tailwind background class — `bg-comp-badge-<variant>-bg`. */
  bg: string;
  /** Tailwind text-color class — `text-comp-badge-<variant>-label`. */
  label: string;
  /**
   * Tailwind background class using the **label** color — used for the leading
   * status dot, which paints in the same color the label text would.
   *
   * Stored as its own literal rather than derived at runtime from `label` via
   * string replacement: Tailwind v4's JIT scanner only emits CSS for class
   * strings it can see as literals in source. A computed `bg-…` string is
   * invisible to the scanner and silently produces no styles.
   */
  labelBg: string;
  /**
   * Tailwind class for the white-tint state-layer overlay. Always `null` now
   * — the `--comp-badge-*-state-layer` tokens were retired from the design
   * system (badges bind only `bg` + `label`), so no variant has one anymore.
   * Kept as `string | null` rather than dropped so a token can come back
   * without widening this field's type again.
   */
  stateLayer: string | null;
  /** Opacity Figma applies to the label text + status dot for this variant. */
  labelOpacity: "opacity-50" | "opacity-60" | "opacity-80" | "opacity-100";
}

/**
 * Variant → token mapping. Consumed internally by {@link Badge} and
 * {@link IconBadge}. Exported so call sites that need to render badge-styled
 * elements outside the component (rare — prefer the components) can pull the
 * exact token classes.
 *
 * Source of truth for the underlying CSS variables is
 * `packages/ui/base/variables.css` (`--comp-badge-*`).
 */
export const BADGE_VARIANTS: Record<BadgeVariant, VariantTokens> = {
  primary: {
    bg: "bg-comp-badge-primary-bg",
    label: "text-comp-badge-primary-label",
    labelBg: "bg-comp-badge-primary-label",
    stateLayer: null,
    /* Figma 4028:214760 paints the status dot and label at full strength
       (`#407878`, no node opacity) — the 60% fade rendered them as a washed
       ~#8fb3b4 over the badge fill, which also made the 4px left padding read
       as misaligned. */
    labelOpacity: "opacity-100",
  },
  positive: {
    bg: "bg-comp-badge-positive-bg",
    label: "text-comp-badge-positive-label",
    labelBg: "bg-comp-badge-positive-label",
    stateLayer: null,
    labelOpacity: "opacity-100",
  },
  secondary: {
    bg: "bg-comp-badge-secondary-bg",
    label: "text-comp-badge-secondary-label",
    labelBg: "bg-comp-badge-secondary-label",
    stateLayer: null,
    /* Figma paints this label (icon included) at full strength — the 50% fade washed
       `--comp-badge-secondary-label` out to a grey that read as disabled. */
    labelOpacity: "opacity-100",
  },
  tertiary: {
    bg: "bg-comp-badge-tertiary-bg",
    label: "text-comp-badge-tertiary-label",
    labelBg: "bg-comp-badge-tertiary-label",
    stateLayer: null,
    labelOpacity: "opacity-80",
  },
  "tertiary-mono": {
    bg: "bg-comp-badge-tertiary-bg",
    label: "text-comp-badge-tertiary-label",
    labelBg: "bg-comp-badge-tertiary-label",
    stateLayer: null,
    labelOpacity: "opacity-80",
  },
  warning: {
    bg: "bg-comp-badge-warning-bg",
    label: "text-comp-badge-warning-label",
    labelBg: "bg-comp-badge-warning-label",
    stateLayer: null,
    labelOpacity: "opacity-80",
  },
  destructive: {
    bg: "bg-comp-badge-destructive-bg",
    label: "text-comp-badge-destructive-label",
    labelBg: "bg-comp-badge-destructive-label",
    stateLayer: null,
    labelOpacity: "opacity-60",
  },
  "accent-1": {
    bg: "bg-comp-badge-accent-1-bg",
    label: "text-comp-badge-accent-1-label",
    labelBg: "bg-comp-badge-accent-1-label",
    stateLayer: null,
    labelOpacity: "opacity-80",
  },
  "accent-2": {
    bg: "bg-comp-badge-accent-2-bg",
    label: "text-comp-badge-accent-2-label",
    labelBg: "bg-comp-badge-accent-2-label",
    stateLayer: null,
    labelOpacity: "opacity-80",
  },
};
